import { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  IconButton, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Button, MenuItem, Grid, Stack,
  FormControl, InputLabel, Select, OutlinedInput, FormControlLabel, Checkbox,
  Alert, List, ListItem, ListItemText, Snackbar, LinearProgress
} from '@mui/material';
import { Edit as EditIcon, UploadFile as UploadFileIcon, InsertDriveFile as FileIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { contentService, dropdownService } from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import { useAuth } from '../context/AuthContext';

import { openScriptSecure } from '../utils/fileUrl';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

const STATUS_OPTIONS = [
  'Content Ready',
  'Content Approved',
  'Planning Completed',
  'Shoot Completed',
  'Editing Completed',
  'Released'
];

const STATUS_ORDER = {
  'Content Ready': 1,
  'Content Approved': 2,
  'Planning Completed': 3,
  'Shoot Completed': 4,
  'Editing Completed': 5,
  'Released': 6
};

function DirectorDashboard() {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [editForm, setEditForm] = useState({
    status: '',
    remarks: '',
    castingList: [],
    castingIds: [],
    contentId: '',
    contentTitle: '',
    channel: '',
    contentType: '',
    genre: '',
    shootDate: '',
    releaseDate: '',
    locationDetails: '',
    directorId: '',
    director: '',
    editorId: '',
    editor: '',
    cinematographerId: '',
    cinematographer: '',
    writerId: '',
    writer: '',
    videoPerformance: '',
    repositoryUploadStatus: 'Not Started',
    ytVideoUrl: '',
    facebookUrl: '',
    integratedAdIncluded: false,
    directorEfforts: []
  });
  const [newDirectorEffortRow, setNewDirectorEffortRow] = useState({ directorId: '', directorName: '', workType: '', effort: '' });
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [actors, setActors] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Script file state
  const [dialogScriptFile, setDialogScriptFile] = useState(null);
  const [scriptUploading, setScriptUploading] = useState(false);
  const [scriptDeleting, setScriptDeleting] = useState(false);

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  function generateMonthOptions() {
    const options = [];
    const now = new Date();
    
    // Current month + previous 11 months (total 12 months)
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label, isCurrent: i === 0 });
    }
    
    return options;
  }

  function filterByMonth(items) {
    const [year, month] = selectedMonth.split('-');
    return items.filter(item => {
      if (!item.shootDate) return false;
      const itemDate = new Date(item.shootDate);
      return itemDate.getFullYear() === parseInt(year) && 
             (itemDate.getMonth() + 1) === parseInt(month);
    });
  }

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (user?.fullName) {
      fetchDirectorContent();
    }
  }, [user, selectedMonth]);

  const fetchDropdowns = async () => {
    try {
      const response = await dropdownService.getAll();
      const data = response.data.data;
      setDropdowns(data);
      if (data.actor) setActors(data.actor);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
    }
  };

  const fetchDirectorContent = async () => {
    setLoading(true);
    try {
      const response = await contentService.getAll({ limit: 1000 });
      const allContent = response.data.data.content || [];
      
      // Filter content where current user is the director (using username/ID now)
      const directorContent = allContent.filter(item => 
        item.directorId === user.username
      );
      
      // Sort by status order (1-5)
      const sortedContent = directorContent.sort((a, b) => {
        return (STATUS_ORDER[a.status] || 0) - (STATUS_ORDER[b.status] || 0);
      });
      
      // Apply month filter
      const filteredContent = filterByMonth(sortedContent);
      
      setContent(filteredContent);
    } catch (error) {
      console.error('Error fetching director content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (item) => {
    let castingIds = item.castingIds || [];
    if (typeof castingIds === 'string') {
      try { castingIds = JSON.parse(castingIds); } catch (e) { castingIds = []; }
    }

    setEditForm({
      contentTitle: item.contentTitle || '',
      contentId: item.contentId || '',
      channel: item.channel || '',
      contentType: item.contentType || '',
      genre: item.genre || '',
      status: item.status || '',
      shootDate: item.shootDate || '',
      releaseDate: item.releaseDate || '',
      remarks: item.remarks || '',
      castingList: item.castingList || [],
      castingIds: Array.isArray(castingIds) ? castingIds : [],
      locationDetails: item.locationDetails || '',
      directorId: item.directorId || '',
      director: item.director || '',
      editorId: item.editorId || '',
      editor: item.editor || '',
      cinematographerId: item.cinematographerId || '',
      cinematographer: item.cinematographer || '',
      writerId: item.writerId || '',
      writer: item.writer || '',
      videoPerformance: item.videoPerformance || '',
      repositoryUploadStatus: item.repositoryUploadStatus || 'Not Started',
      ytVideoUrl: item.ytVideoUrl || '',
      facebookUrl: item.facebookUrl || '',
      integratedAdIncluded: Boolean(item.integratedAdIncluded),
      directorEfforts: (() => { try { const v = item.directorEfforts; return Array.isArray(v) ? v : (v ? JSON.parse(v) : []); } catch (_) { return []; } })(),
    });
    setEditDialog({ open: true, item });
    // Load script file metadata
    try {
      const sf = item.scriptFile;
      setDialogScriptFile(sf ? (typeof sf === 'string' ? JSON.parse(sf) : sf) : null);
    } catch (_) { setDialogScriptFile(null); }
  };

  const handleDialogScriptUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !editDialog.item?.id) return;
    setScriptUploading(true);
    try {
      const fd = new FormData();
      fd.append('script', file);
      const res = await contentService.uploadScript(editDialog.item.id, fd);
      setDialogScriptFile(res.data.data.scriptFile);
      setSnackbar({ open: true, message: 'Script uploaded!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Upload failed', severity: 'error' });
    } finally { setScriptUploading(false); e.target.value = ''; }
  };

  const handleDialogScriptDelete = async () => {
    if (!window.confirm('Remove the script file?')) return;
    setScriptDeleting(true);
    try {
      await contentService.deleteScript(editDialog.item.id);
      setDialogScriptFile(null);
      setSnackbar({ open: true, message: 'Script removed', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Delete failed', severity: 'error' });
    } finally { setScriptDeleting(false); }
  };

  const handleSaveEdit = async () => {
    const originalStatus = editDialog.item?.status;
    const newStatus = editForm.status;
    const STATUS_LIST = ['Content Ready', 'Content Approved', 'Planning Completed', 'Shoot Completed', 'Editing Completed', 'Released'];
    const REVISION_STATUS = 'Sent for Revision';
    const errors = [];

    // --- Status transition validation ---
    if (newStatus !== originalStatus) {
      if (originalStatus === 'Released') {
        errors.push({ field: 'Status', message: 'Cannot change status once content is "Released".' });
      } else if (newStatus === REVISION_STATUS) {
        if (originalStatus !== 'Content Approved' && originalStatus !== 'Planning Completed') {
          errors.push({ field: 'Status', message: `"Sent for Revision" can only be set from "Content Approved" or "Planning Completed". Current status is "${originalStatus}".` });
        }
      } else if (originalStatus === REVISION_STATUS) {
        if (newStatus !== 'Content Approved' && newStatus !== 'Content Ready') {
          errors.push({ field: 'Status', message: `From "Sent for Revision" you can only move to "Content Ready" or "Content Approved".` });
        }
      } else {
        const oldIdx = STATUS_LIST.indexOf(originalStatus);
        const newIdx = STATUS_LIST.indexOf(newStatus);
        if (oldIdx !== -1 && newIdx !== -1) {
          if (newIdx > oldIdx + 1) {
            const required = STATUS_LIST.slice(oldIdx + 1, newIdx).join(' → ');
            errors.push({ field: 'Status', message: `Cannot skip from "${originalStatus}" to "${newStatus}". Must go through: ${required}` });
          } else if (newIdx < oldIdx && oldIdx > 2) {
            errors.push({ field: 'Status', message: `Cannot go back from "${originalStatus}". Status changes are locked once Shoot is completed.` });
          }
        }
      }
    }

    // --- Field validations based on target status ---
    if (!editForm.genre) {
      errors.push({ field: 'Genre', message: 'Genre is required.' });
    }
    if (!editForm.shootDate) {
      errors.push({ field: 'Shoot Date', message: 'Shoot Date is required (enter a tentative date if not confirmed).' });
    }

    if (newStatus === 'Planning Completed') {
      if (!editForm.castingIds || editForm.castingIds.length === 0)
        errors.push({ field: 'Casting List', message: 'Required when status is "Planning Completed"' });
      if (!editForm.cinematographerId)
        errors.push({ field: 'Cinematographer', message: 'Required when status is "Planning Completed"' });
    }

    if (newStatus === 'Shoot Completed') {
      if (!editForm.castingIds || editForm.castingIds.length === 0)
        errors.push({ field: 'Casting List', message: 'Required when status is "Shoot Completed"' });
      if (!editForm.directorId)
        errors.push({ field: 'Director', message: 'Required when status is "Shoot Completed"' });
      if (!editForm.cinematographerId)
        errors.push({ field: 'Cinematographer', message: 'Required when status is "Shoot Completed"' });
      if (!editForm.shootDate)
        errors.push({ field: 'Shoot Date', message: 'Required when status is "Shoot Completed"' });
      const _dirEffortMin = editForm.contentType === 'Long Video' ? 3 : 1;
      if ((editForm.directorEfforts || []).length < _dirEffortMin) {
        errors.push({ field: "Director's Effort", message: `At least ${_dirEffortMin} Director's Effort ${_dirEffortMin === 1 ? 'entry' : 'entries'} required for ${editForm.contentType || 'this content type'} when marking Shoot Completed` });
      }
    }

    if (newStatus === 'Editing Completed') {
      if (!editForm.castingIds || editForm.castingIds.length === 0)
        errors.push({ field: 'Casting List', message: 'Required when status is "Editing Completed"' });
      if (!editForm.directorId)
        errors.push({ field: 'Director', message: 'Required when status is "Editing Completed"' });
      if (!editForm.cinematographerId)
        errors.push({ field: 'Cinematographer', message: 'Required when status is "Editing Completed"' });
      if (!editForm.editorId)
        errors.push({ field: 'Editor', message: 'Required when status is "Editing Completed"' });
      if (!editForm.shootDate)
        errors.push({ field: 'Shoot Date', message: 'Required when status is "Editing Completed"' });
    }

    if (newStatus === 'Released') {
      const ytdValues = ['Yet to Decide', 'Yet To Decide', 'yet to decide', 'YET TO DECIDE'];
      if (!editForm.channel || ytdValues.some(v => editForm.channel.trim().toLowerCase() === v.toLowerCase()))
        errors.push({ field: 'Channel', message: 'Channel cannot be "Yet to Decide" when releasing.' });
      if (editForm.contentId && /^(YET|XXX)-/i.test(editForm.contentId.trim()))
        errors.push({ field: 'Content ID', message: 'Content ID still has a placeholder prefix (YET/XXX). Please assign a proper channel before releasing.' });
      if (!editForm.castingIds || editForm.castingIds.length === 0)
        errors.push({ field: 'Casting List', message: 'Required when status is "Released"' });
      if (!editForm.directorId)
        errors.push({ field: 'Director', message: 'Required when status is "Released"' });
      if (!editForm.cinematographerId)
        errors.push({ field: 'Cinematographer', message: 'Required when status is "Released"' });
      if (!editForm.editorId)
        errors.push({ field: 'Editor', message: 'Required when status is "Released"' });
      if (!editForm.shootDate)
        errors.push({ field: 'Shoot Date', message: 'Required when status is "Released"' });
      if (!editForm.releaseDate)
        errors.push({ field: 'Release Date', message: 'Required when status is "Released"' });
      if (!editForm.ytVideoUrl || !editForm.ytVideoUrl.trim())
        errors.push({ field: 'YouTube Video URL', message: 'Required when status is "Released"' });
    }

    if (errors.length > 0) {
      setValidationErrors(errors);
      setErrorDialogOpen(true);
      return;
    }

    try {
      const payload = { ...editForm };
      if (!payload.contentId || payload.contentId === editDialog.item?.contentId) delete payload.contentId;
      if (!payload.shootDate) payload.shootDate = null;
      if (!payload.releaseDate) payload.releaseDate = null;
      await contentService.update(editDialog.item.id, payload);
      setSnackbar({ open: true, message: 'Content updated successfully!', severity: 'success' });
      setEditDialog({ open: false, item: null });
      fetchDirectorContent();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Error updating content', severity: 'error' });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Content Ready': 'info',
      'Content Approved': 'warning',
      'Planning Completed': 'default',
      'Shoot Completed': 'warning',
      'Editing Completed': 'secondary',
      'Released': 'success'
    };
    return colors[status] || 'default';
  };

  const handleCastingChange = (e) => {
    const { value } = e.target;
    const selectedUsernames = typeof value === 'string' ? value.split(',') : value;
    const castingNames = selectedUsernames.map(username => {
      const actor = actors.find(a => a.username === username);
      return actor ? actor.firstName || actor.name : username;
    });
    setEditForm(prev => ({ ...prev, castingIds: selectedUsernames, castingList: castingNames }));
  };

  const handleUserSelect = (field, idField) => (e) => {
    const selectedUsername = e.target.value;
    const users = dropdowns[field] || [];
    const selectedUser = users.find(u => u.username === selectedUsername);
    setEditForm(prev => ({
      ...prev,
      [idField]: selectedUsername,
      [field]: selectedUser ? (selectedUser.firstName || selectedUser.name) : ''
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          🎬 Director's Dashboard
        </Typography>
        <TextField
          select
          size="small"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          sx={{ minWidth: 200 }}
          label="Filter by Month"
        >
          {generateMonthOptions().map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label} {option.isCurrent && '(Current)'}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Statistics Card */}
      <Grid container spacing={1} mb={2}>
        <Grid item xs={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Typography variant="h4" fontWeight={700}>{content.length}</Typography>
              <Typography variant="caption">Assigned Content</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Typography variant="h4" fontWeight={700}>
                {content.filter(c => ['Content Ready', 'Content Approved', 'Planning Completed'].includes(c.status)).length}
              </Typography>
              <Typography variant="caption">Pre-Production</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Typography variant="h4" fontWeight={700}>
                {content.filter(c => c.status === 'Shoot Completed' || c.status === 'Editing Completed').length}
              </Typography>
              <Typography variant="caption">Post-Production</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent sx={{ py: 1.5, px: 1.5 }}>
              <Typography variant="h4" fontWeight={700}>
                {content.filter(c => c.status === 'Released').length}
              </Typography>
              <Typography variant="caption">Released</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Content List */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            📋 My Assigned Content
          </Typography>
          
          {/* Desktop Table View */}
          <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Title</strong></TableCell>
                  <TableCell><strong>Channel</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Shoot Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Casting</strong></TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}><strong>Editor</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {content.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography color="text.secondary" py={3}>
                        No content assigned to you yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  content.map((item) => (
                    <TableRow 
                      key={item.id} 
                      hover
                      onClick={() => handleEditClick(item)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{item.contentTitle}</TableCell>
                      <TableCell>
                        <Chip label={item.channel} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{item.contentType}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatDate(item.shootDate) || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          color={getStatusColor(item.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {item.castingList && item.castingList.length > 0
                          ? item.castingList.join(', ')
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{item.editor || '-'}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(item)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {content.length === 0 ? (
              <Typography color="text.secondary" py={3} align="center">
                No content assigned to you yet.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {content.map((item) => (
                  <Card 
                    key={item.id} 
                    variant="outlined"
                    onClick={() => handleEditClick(item)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                        <Box flex={1}>
                          <Typography variant="caption" color="primary" fontWeight="bold">
                            {item.contentId || 'No ID'}
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {item.contentTitle}
                          </Typography>
                        </Box>
                        <Box onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditClick(item)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        <Chip label={item.channel} size="small" color="primary" variant="outlined" />
                        <Chip label={item.contentType} size="small" color="secondary" variant="outlined" />
                      </Box>
                      
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Director:
                          </Typography>
                          <Typography variant="body2">
                            {item.director || '-'}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Shoot Date:
                          </Typography>
                          <Typography variant="body2">
                            {formatDate(item.shootDate) || '-'}
                          </Typography>
                        </Box>
                        
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          <Chip
                            label={item.status}
                            color={getStatusColor(item.status)}
                            size="small"
                          />
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, item: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Content: {editDialog.item?.contentTitle}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Content ID — read-only */}
            {editForm.contentId && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Content ID"
                  value={editForm.contentId}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-updates when Channel or Content Type changes"
                  sx={{ '& .MuiInputBase-input': { color: 'primary.main', fontWeight: 600 } }}
                />
              </Grid>
            )}

            {/* Content Title */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Content Title"
                value={editForm.contentTitle}
                onChange={(e) => setEditForm({ ...editForm, contentTitle: e.target.value })}
              />
            </Grid>

            {/* Channel */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={editForm.channel}
                  onChange={(e) => {
                    const newChannel = e.target.value;
                    const channelCode = dropdowns.channelIds?.[newChannel] || newChannel.substring(0, 3).toUpperCase().replace(/\s/g, '');
                    const typeCode = dropdowns.contentTypeIds?.[editForm.contentType] || editForm.contentType.substring(0, 2).toUpperCase().replace(/\s/g, '');
                    const parts = (editForm.contentId || '').split('-');
                    const newContentId = parts.length >= 4
                      ? `${channelCode}-${typeCode}-${parts[parts.length - 2]}-${parts[parts.length - 1]}`
                      : editForm.contentId;
                    setEditForm({ ...editForm, channel: newChannel, contentId: newContentId });
                  }}
                  label="Channel"
                >
                  {(dropdowns.channels || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Content Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Content Type</InputLabel>
                <Select
                  value={editForm.contentType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const channelCode = dropdowns.channelIds?.[editForm.channel] || editForm.channel.substring(0, 3).toUpperCase().replace(/\s/g, '');
                    const typeCode = dropdowns.contentTypeIds?.[newType] || newType.substring(0, 2).toUpperCase().replace(/\s/g, '');
                    const parts = (editForm.contentId || '').split('-');
                    const newContentId = parts.length >= 4
                      ? `${channelCode}-${typeCode}-${parts[parts.length - 2]}-${parts[parts.length - 1]}`
                      : editForm.contentId;
                    setEditForm({ ...editForm, contentType: newType, contentId: newContentId });
                  }}
                  label="Content Type"
                >
                  {(dropdowns.contentTypes || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Genre */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Genre</InputLabel>
                <Select
                  value={editForm.genre}
                  onChange={(e) => setEditForm({ ...editForm, genre: e.target.value })}
                  label="Genre"
                >
                  {(dropdowns.genres || []).map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Status"
                value={editForm.status}
                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
              >
                {STATUS_OPTIONS.filter(s => {
                  const cur = editDialog.item?.status;
                  const curIdx = STATUS_OPTIONS.indexOf(cur);
                  const sIdx = STATUS_OPTIONS.indexOf(s);
                  return sIdx === curIdx || sIdx === curIdx + 1;
                }).map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Casting List */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Casting List</InputLabel>
                <Select
                  multiple
                  value={editForm.castingIds}
                  onChange={handleCastingChange}
                  input={<OutlinedInput label="Casting List" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((username) => {
                        const actor = actors.find(a => a.username === username);
                        return <Chip key={username} label={actor ? actor.name : username} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {actors.map((actor) => (
                    <MenuItem key={actor.username} value={actor.username}>{actor.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editForm.castingIds.length > 0 && (
                <Button size="small" color="error"
                  onClick={() => setEditForm(prev => ({ ...prev, castingIds: [], castingList: [] }))}
                  sx={{ mt: 1 }}>
                  Clear All Casting
                </Button>
              )}
            </Grid>

            {/* Location Details */}
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={2} label="Location Details"
                value={editForm.locationDetails}
                onChange={(e) => setEditForm({ ...editForm, locationDetails: e.target.value })}
              />
            </Grid>

            {/* Shoot Date / Release Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Shoot Date" type="date"
                value={editForm.shootDate}
                onChange={(e) => setEditForm({ ...editForm, shootDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Date when shooting was completed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Release Date" type="date"
                value={editForm.releaseDate}
                onChange={(e) => setEditForm({ ...editForm, releaseDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Date when content was released"
              />
            </Grid>

            {/* Director */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Director</InputLabel>
                <Select
                  value={editForm.directorId}
                  onChange={handleUserSelect('director', 'directorId')}
                  label="Director"
                >
                  <MenuItem value="">None</MenuItem>
                  {(dropdowns.director || []).map(u => (
                    <MenuItem key={u.username} value={u.username}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Editor */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Editor</InputLabel>
                <Select
                  value={editForm.editorId}
                  onChange={handleUserSelect('editor', 'editorId')}
                  label="Editor"
                >
                  <MenuItem value="">None</MenuItem>
                  {(dropdowns.editor || []).map(u => (
                    <MenuItem key={u.username} value={u.username}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Cinematographer */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cinematographer</InputLabel>
                <Select
                  value={editForm.cinematographerId}
                  onChange={handleUserSelect('cinematographer', 'cinematographerId')}
                  label="Cinematographer"
                >
                  <MenuItem value="">None</MenuItem>
                  {(dropdowns.cinematographer || []).map(u => (
                    <MenuItem key={u.username} value={u.username}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Writer */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Writer</InputLabel>
                <Select
                  value={editForm.writerId}
                  onChange={handleUserSelect('writer', 'writerId')}
                  label="Writer"
                >
                  <MenuItem value="">None</MenuItem>
                  {(dropdowns.writer || []).map(u => (
                    <MenuItem key={u.username} value={u.username}>{u.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Video Performance */}
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Video Performance"
                value={editForm.videoPerformance}
                onChange={(e) => setEditForm({ ...editForm, videoPerformance: e.target.value })}
              >
                <MenuItem value="">— None —</MenuItem>
                {['Below Average','Average','Good','Excellent'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Repository Upload Status */}
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Repository Upload Status"
                value={editForm.repositoryUploadStatus}
                onChange={(e) => setEditForm({ ...editForm, repositoryUploadStatus: e.target.value })}
              >
                {['Not Started','In Progress','Completed','Failed'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>

            {/* YouTube Video URL */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="YouTube Video URL"
                value={editForm.ytVideoUrl}
                onChange={(e) => setEditForm({ ...editForm, ytVideoUrl: e.target.value })}
                placeholder="https://youtu.be/..."
              />
            </Grid>

            {/* Facebook Video URL */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Facebook Video URL"
                value={editForm.facebookUrl}
                onChange={(e) => setEditForm({ ...editForm, facebookUrl: e.target.value })}
                placeholder="Optional"
              />
            </Grid>

            {/* Integrated Ad — Long Video only */}
            {editForm.contentType === 'Long Video' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(editForm.integratedAdIncluded)}
                      onChange={(e) => setEditForm(prev => ({ ...prev, integratedAdIncluded: e.target.checked }))}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Integrated Ad Included</Typography>
                      <Typography variant="caption" color="text.secondary">Check if this Long Video contains an integrated advertisement</Typography>
                    </Box>
                  }
                />
              </Grid>
            )}

            {/* Director's Effort */}
            <Grid item xs={12}>
              <Box sx={{ border: '2px solid', borderColor: '#0f766e', borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ background: 'linear-gradient(135deg, #0f766e 0%, #0891b2 100%)', px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#fff', letterSpacing: 0.5 }}>🎬 Director's Effort</Typography>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f0fdfa' }}>
                  <Grid container spacing={1} alignItems="center" mb={1.5}>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Director</InputLabel>
                        <Select
                          value={newDirectorEffortRow.directorId}
                          label="Director"
                          onChange={(e) => {
                            const dr = (dropdowns.director || []).find(d => d.username === e.target.value);
                            setNewDirectorEffortRow(r => ({ ...r, directorId: e.target.value, directorName: dr ? dr.firstName + (dr.lastName ? ' ' + dr.lastName : '') : e.target.value }));
                          }}
                        >
                          {(dropdowns.director || []).map(dr => (
                            <MenuItem key={dr.username} value={dr.username}>{dr.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Work Type</InputLabel>
                        <Select
                          value={newDirectorEffortRow.workType}
                          label="Work Type"
                          onChange={(e) => setNewDirectorEffortRow(r => ({ ...r, workType: e.target.value }))}
                        >
                          {(dropdowns.director_work_types || []).map(w => (
                            <MenuItem key={w} value={w}>{w}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={8} sm={4}>
                      <TextField
                        size="small" fullWidth label="Effort (hours)"
                        value={newDirectorEffortRow.effort}
                        onChange={(e) => setNewDirectorEffortRow(r => ({ ...r, effort: e.target.value }))}
                        placeholder="e.g. 2.5"
                      />
                    </Grid>
                    <Grid item xs={4} sm={2}>
                      <Button
                        variant="contained" size="small" startIcon={<AddIcon />} fullWidth
                        sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d6659' } }}
                        disabled={!newDirectorEffortRow.directorId || !newDirectorEffortRow.workType || !newDirectorEffortRow.effort}
                        onClick={() => {
                          const isDuplicate = (editForm.directorEfforts || []).some(r => r.directorId === newDirectorEffortRow.directorId && r.workType === newDirectorEffortRow.workType);
                          if (isDuplicate) { alert(`${newDirectorEffortRow.directorName || newDirectorEffortRow.directorId} already has a "${newDirectorEffortRow.workType}" entry.`); return; }
                          setEditForm(prev => ({ ...prev, directorEfforts: [...(prev.directorEfforts || []), { ...newDirectorEffortRow }] }));
                          setNewDirectorEffortRow({ directorId: '', directorName: '', workType: '', effort: '' });
                        }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                  {(editForm.directorEfforts || []).length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ borderColor: '#99f6e4' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#ccfbf1' }}>
                            <TableCell sx={{ color: '#0f766e', fontWeight: 700 }}>Director</TableCell>
                            <TableCell sx={{ color: '#0f766e', fontWeight: 700 }}>Work Type</TableCell>
                            <TableCell sx={{ color: '#0f766e', fontWeight: 700 }}>Effort (hrs)</TableCell>
                            <TableCell width={48}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(editForm.directorEfforts || []).map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f0fdfa' } }}>
                              <TableCell>{row.directorName || row.directorId}</TableCell>
                              <TableCell><Chip label={row.workType} size="small" sx={{ bgcolor: '#ccfbf1', color: '#0f766e', fontWeight: 600 }} /></TableCell>
                              <TableCell><strong>{row.effort}</strong> hrs</TableCell>
                              <TableCell>
                                <IconButton size="small" color="error"
                                  onClick={() => setEditForm(prev => ({ ...prev, directorEfforts: prev.directorEfforts.filter((_, i) => i !== idx) }))}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={4} label="Remarks"
                value={editForm.remarks}
                onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                placeholder="Add notes or comments about this content..."
              />
            </Grid>

            {/* Script / Content File */}
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1.5} display="flex" alignItems="center" gap={1}>
                  <FileIcon fontSize="small" color="primary" /> Script / Content File
                </Typography>
                {dialogScriptFile ? (
                  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <Chip icon={<FileIcon />} label={dialogScriptFile.originalName} color="primary" variant="outlined"
                      onClick={() => openScriptSecure(editDialog.item?.id)}
                      sx={{ cursor: 'pointer', maxWidth: 280 }} />
                    <Typography variant="caption" color="text.secondary">
                      {(dialogScriptFile.size / 1024).toFixed(1)} KB · {dialogScriptFile.uploadedBy} · {new Date(dialogScriptFile.uploadedAt).toLocaleDateString('en-IN')}
                    </Typography>
                    <Button size="small" variant="outlined" component="label"
                      startIcon={<UploadFileIcon />} disabled={scriptUploading}>
                      Replace
                      <input type="file" hidden accept=".pdf,.doc,.docx,.txt" onChange={handleDialogScriptUpload} />
                    </Button>
                    <Button size="small" variant="outlined" color="error"
                      startIcon={scriptDeleting ? <CircularProgress size={14} /> : <DeleteIcon />}
                      disabled={scriptDeleting} onClick={handleDialogScriptDelete}>
                      Remove
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Button variant="outlined" component="label"
                      startIcon={scriptUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                      disabled={scriptUploading}>
                      {scriptUploading ? 'Uploading…' : 'Upload Script / Content File'}
                      <input type="file" hidden accept=".pdf,.doc,.docx,.txt" onChange={handleDialogScriptUpload} />
                    </Button>
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      PDF, Word (.doc/.docx), TXT · Max 20 MB
                    </Typography>
                    {scriptUploading && <LinearProgress sx={{ mt: 1 }} />}
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, item: null })}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validation Errors Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white' }}>
          ⚠️ Validation Errors
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please fix the following errors:
          </Typography>
          <List>
            {validationErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="error">
                      • <strong>{error.field}:</strong> {error.message}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)} variant="contained" color="primary">
            OK, I'll Fix It
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default DirectorDashboard;
